// src/components/Properties/GroupProperties/GroupProperties.tsx
// FIXED: Access icons from modelStore, not scene
import React, { useState, useMemo } from 'react';
import {
  Users,
  Lock,
  Unlock,
  Trash2,
  Edit2,
  X,
  Link,
  Square,
  Type
} from 'lucide-react';
import { useGroupStore } from 'src/stores/groupStore';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { useModelStore } from 'src/stores/modelStore';
import { useScene } from 'src/hooks/useScene';
import { GroupableElement } from 'src/features/groups/groupTypes';

interface GroupPropertiesProps {
  groupId: string;
}

// Helper to get icon for element type (for non-ITEM types)
const getElementIcon = (type: string) => {
  switch (type) {
    case 'CONNECTOR':
      return Link;
    case 'RECTANGLE':
      return Square;
    case 'TEXTBOX':
      return Type;
    default:
      return null; // For ITEM, we'll show the actual icon
  }
};

// Helper to get element display name and icon info
const getElementDisplayInfo = (
  element: GroupableElement,
  scene: any,
  modelStore: any
) => {
  try {
    switch (element.type) {
      case 'ITEM': {
        const item = scene.items.find((i: any) => i.id === element.id);
        if (!item) {
          console.warn('❌ Item not found in scene:', element.id);
          return {
            name: `Item ${element.id.substring(0, 8)}`,
            typeLabel: 'Icon',
            iconId: null
          };
        }

        // The item's id IS the model ID (no separate model property)
        const modelItem = modelStore.items.find(
          (mi: any) => mi.id === item.id
        );

        if (!modelItem) {
          console.warn('❌ ModelItem not found for item id:', item.id);
          return {
            name: `Item ${element.id.substring(0, 8)}`,
            typeLabel: 'Icon',
            iconId: null
          };
        }

        if (!modelItem.icon) {
          console.warn('❌ ModelItem has no icon:', modelItem);
        }

        return {
          name: modelItem.name || `Item ${element.id.substring(0, 8)}`,
          typeLabel: 'Icon',
          iconId: modelItem.icon || null
        };
      }
      case 'CONNECTOR': {
        const connector = scene.connectors.find(
          (c: any) => c.id === element.id
        );
        return {
          name:
            connector?.description || `Connector ${element.id.substring(0, 8)}`,
          typeLabel: 'Connector',
          iconId: null
        };
      }
      case 'RECTANGLE':
        return {
          name: `Rectangle ${element.id.substring(0, 8)}`,
          typeLabel: 'Rectangle',
          iconId: null
        };
      case 'TEXTBOX': {
        const textbox = scene.textBoxes.find((tb: any) => tb.id === element.id);
        const content = textbox?.content?.substring(0, 20);
        return {
          name: content
            ? `"${content}${textbox.content.length > 20 ? '...' : ''}"`
            : `Text ${element.id.substring(0, 8)}`,
          typeLabel: 'Text',
          iconId: null
        };
      }
      default:
        return {
          name: `${element.type} ${element.id.substring(0, 8)}`,
          typeLabel: element.type,
          iconId: null
        };
    }
  } catch (error) {
    console.error('Error getting element display info:', error);
    return {
      name: `${element.type} ${element.id.substring(0, 8)}`,
      typeLabel: element.type,
      iconId: null
    };
  }
};

// Component to display icon thumbnail for ITEM type
const IconThumbnail: React.FC<{
  iconId: string;
  name: string;
  icons: any[];
}> = ({ iconId, name, icons }) => {
  const icon = icons.find((i: any) => i.id === iconId);

  if (!icon?.url) {
    console.warn('❌ Icon not found or has no URL:', iconId, 'Available icons:', icons.map((i: any) => i.id));
    // Fallback: show first letter in a colored circle
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded bg-blue-100 text-sm font-semibold text-blue-600 dark:bg-blue-950 dark:text-blue-400">
        {name.charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 p-0.5 dark:bg-slate-700">
      <img
        src={icon.url}
        alt={name}
        className="h-full w-full object-contain"
        onError={(e) => {
          // Fallback if image fails to load
          e.currentTarget.style.display = 'none';
        }}
      />
    </div>
  );
};

export const GroupProperties: React.FC<GroupPropertiesProps> = ({
  groupId
}) => {
  const groupActions = useGroupStore((state) => state.actions);
  const groups = useGroupStore((state) => state.groups);
  const uiStateActions = useUiStateStore((state) => state.actions);
  const itemControls = useUiStateStore((state) => state.itemControls);
  const scene = useScene();

  // ✅ FIX: Get icons from modelStore
  const modelStore = useModelStore((state) => state);
  const icons = modelStore.icons || [];

  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');

  const group = useMemo(() => {
    return groups.find((g) => g.id === groupId);
  }, [groups, groupId]);

  // Check if there are selected items NOT in this group AND not in any other group
  const itemsNotInGroup = useMemo(() => {
    if (!itemControls || itemControls.type !== 'MULTI_SELECT') {
      return [];
    }

    const groupElementKeys = new Set(
      group?.elements.map(el => `${el.type}-${el.id}`) || []
    );

    // Filter for items that are NOT in this group AND NOT in any other group
    const notInGroup = itemControls.items.filter(item => {
      const key = `${item.type}-${item.id}`;
      if (groupElementKeys.has(key)) {
        return false; // Item is in current group
      }

      // Check if item is in any other group
      const element: GroupableElement = { type: item.type as any, id: item.id };
      const itemGroup = groupActions.getGroupForElement(element);

      // Only include if item is NOT in any group
      return !itemGroup;
    });

    return notInGroup;
  }, [itemControls, group, groupActions]);

  if (!group) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <Users className="mx-auto h-12 w-12 opacity-50" />
          <p className="mt-2 text-sm">Group not found</p>
        </div>
      </div>
    );
  }

  const handleRename = () => {
    if (tempName.trim() && tempName !== group.name) {
      const result = groupActions.renameGroup(groupId, tempName.trim());
      if (result.success) {
        setIsEditingName(false);
        groupActions.refreshGroups();
      }
    } else {
      setIsEditingName(false);
    }
  };

  const handleToggleLocked = () => {
    const newLockedState = !group.locked;
    groupActions.setLocked(groupId, newLockedState);

    // When unlocking, clear multi-selection and select just first element
    if (!newLockedState && itemControls?.type === 'MULTI_SELECT') {
      const firstElement = group.elements[0];
      if (firstElement) {
        uiStateActions.setItemControls({
          type: firstElement.type as any,
          id: firstElement.id
        });
      }
    } else if (newLockedState) {
      // When locking, select all group members
      const groupItems = group.elements.map((el) => ({
        type: el.type as any,
        id: el.id
      }));
      if (groupItems.length > 1) {
        uiStateActions.setItemControls({
          type: 'MULTI_SELECT',
          items: groupItems
        });
      }
    }
  };

  const handleUngroup = () => {
    const result = groupActions.ungroup(groupId);
    if (result.success) {
      uiStateActions.setItemControls(null);
    }
  };

  const handleStartEdit = () => {
    setTempName(group.name);
    setIsEditingName(true);
  };

  const handleAddSelectedToGroup = () => {
    if (itemsNotInGroup.length === 0) return;

    itemsNotInGroup.forEach(item => {
      const element: GroupableElement = { type: item.type as any, id: item.id };
      groupActions.addToGroup(groupId, element);
    });

    // Refresh groups to update UI
    groupActions.refreshGroups();
  };

  const handleRemoveElement = (element: GroupableElement) => {
    const result = groupActions.removeFromGroup(element);

    if (result.success) {
      const updatedGroup = groups.find((g) => g.id === groupId);
      if (updatedGroup && updatedGroup.elements.length < 2) {
        groupActions.ungroup(groupId);
        uiStateActions.setItemControls(null);
      } else if (updatedGroup) {
        const remainingElements = updatedGroup.elements.map((el) => ({
          type: el.type,
          id: el.id
        }));

        if (remainingElements.length > 1) {
          uiStateActions.setItemControls({
            type: 'MULTI_SELECT',
            items: remainingElements
          });
        } else if (remainingElements.length === 1) {
          uiStateActions.setItemControls(remainingElements[0]);
        }
      }
    } else {
      console.error('❌ Failed to remove element:', result.error);
    }
  };

  return (
    <div className="flex h-full flex-col bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 dark:border-slate-700">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-950">
              <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Group Properties
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {group.elements.length} element
                {group.elements.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Lock Button */}
          <button
            onClick={handleToggleLocked}
            className={`flex h-10 w-10 items-center justify-center rounded-md border transition-colors ${
              group.locked
                ? 'border-red-300 bg-red-50 text-red-600 dark:border-red-600 dark:bg-red-950 dark:text-red-400'
                : 'border-gray-300 bg-white text-gray-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400'
            }`}
            title={group.locked ? 'Unlock group' : 'Lock group'}
          >
            {group.locked ? <Lock size={18} /> : <Unlock size={18} />}
          </button>
        </div>
      </div>

      {/* Properties Content */}
      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        {/* Group Name */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Group Name
          </label>
          {isEditingName ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onBlur={handleRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename();
                  if (e.key === 'Escape') setIsEditingName(false);
                }}
                autoFocus
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
              />
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-md border border-gray-300 px-3 py-2 dark:border-slate-600">
              <span className="text-sm text-gray-900 dark:text-white">
                {group.name}
              </span>
              <button
                onClick={handleStartEdit}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700 dark:hover:text-gray-300"
                title="Rename group"
              >
                <Edit2 size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Add Selected Items to Group Button */}
        {itemsNotInGroup.length > 0 && (
          <div className="rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 p-3 dark:border-blue-700 dark:bg-blue-950">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                  {itemsNotInGroup.length} selected item{itemsNotInGroup.length !== 1 ? 's' : ''} not in group
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  Click to add to "{group.name}"
                </p>
              </div>
              <button
                onClick={handleAddSelectedToGroup}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
              >
                <Users size={16} />
                Add to Group
              </button>
            </div>
          </div>
        )}

        {/* ✨ IMPROVED: Individual Elements List */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Group Elements
          </label>
          <div className="space-y-2">
            {group.elements.map((element) => {
              const displayInfo = getElementDisplayInfo(
                element,
                scene,
                modelStore
              );
              const TypeIcon = getElementIcon(element.type);

              return (
                <div
                  key={`${element.type}-${element.id}`}
                  className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 transition-colors hover:border-gray-300 hover:bg-gray-100 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600 dark:hover:bg-slate-750"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Icon/Thumbnail */}
                    {element.type === 'ITEM' && displayInfo.iconId ? (
                      <IconThumbnail
                        iconId={displayInfo.iconId}
                        name={displayInfo.name}
                        icons={icons}
                      />
                    ) : TypeIcon ? (
                      <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-200 dark:bg-slate-700">
                        <TypeIcon
                          size={18}
                          className="text-gray-500 dark:text-gray-400"
                        />
                      </div>
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded bg-blue-100 text-sm font-semibold text-blue-600 dark:bg-blue-950 dark:text-blue-400">
                        {displayInfo.name.charAt(0).toUpperCase()}
                      </div>
                    )}

                    {/* Name and Type */}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {displayInfo.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {displayInfo.typeLabel}
                      </p>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemoveElement(element)}
                    className="ml-2 flex-shrink-0 rounded p-1.5 text-red-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
                    title="Remove from group"
                  >
                    <X size={16} />
                  </button>
                </div>
              );
            })}
          </div>
          {group.elements.length < 2 && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              ⚠️ Groups need at least 2 elements
            </p>
          )}
        </div>

        {/* Danger Zone */}
        <div className="border-t border-gray-200 pt-6 dark:border-slate-700">
          <button
            onClick={handleUngroup}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 dark:border-red-800 dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-900"
          >
            <Trash2 size={16} />
            Ungroup All Elements
          </button>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            This will remove the group but keep all elements
          </p>
        </div>
      </div>
    </div>
  );
};
